import type { Guia } from '../tipos';

export const guiaMaternidade: Guia = {
  id: 'maternidade',
  titulo: 'Maternidade: internação, parto e alta',
  resumo:
    'Internar a gestante, testes da admissão, registrar o parto e os recém-nascidos, dose da mãe e alta.',
  perfis: ['enfermeiro', 'medico'],
  revisadoEm: '2026-10-17',
  secoes: [
    {
      id: 'painel',
      titulo: 'Painel da maternidade',
      passos: [
        'No menu, abra **Maternidade**: aparecem as gestantes internadas e o que falta em cada uma.',
        'Se o seu usuário é de uma UBS, o painel mostra as maternidades do seu município; para ver uma só, escolha em **Unidade**.',
      ],
      imagem: {
        arquivo: 'maternidade-painel.png',
        descricao: 'Painel da maternidade com as gestantes internadas e as pendências.',
      },
    },
    {
      id: 'internar',
      titulo: 'Internar',
      passos: [
        'Abra a ficha da gestante e a aba **Parto e RN**.',
        'Clique em **Registrar internação**, informe a data e a maternidade e salve.',
      ],
      botoes: ['Registrar internação'],
    },
    {
      id: 'admissao',
      titulo: 'Testes da admissão',
      passos: [
        'O protocolo recomenda testar sífilis e HIV em toda gestante na admissão para o parto.',
        'Em **Testes da admissão**, use **Teste rápido** ou **VDRL / outro exame**.',
      ],
      observacao:
        'Sífilis descoberta só no parto: a mãe é notificada como sífilis em gestante, e a ficha do bebê marca o diagnóstico materno "no momento do parto".',
      botoes: ['Teste rápido', 'VDRL / outro exame'],
      imagem: {
        arquivo: 'maternidade-aba-parto.png',
        descricao:
          'Aba Parto e RN com a internação, os testes da admissão, o parto e os recém-nascidos.',
      },
    },
    {
      id: 'parto',
      titulo: 'Parto e recém-nascido',
      passos: [
        'Clique em **Registrar parto** e informe a data e o tipo de parto.',
        'Clique em **Registrar recém-nascido** e preencha os dados do nascimento. Em gemelares, use **Registrar outro (gemelar)**.',
        'Natimorto e aborto também são registrados aqui: entram na notificação de sífilis congênita quando a mãe tem sífilis não tratada adequadamente.',
      ],
      botoes: ['Registrar parto', 'Registrar recém-nascido', 'Registrar outro (gemelar)'],
    },
    {
      id: 'dose-mae',
      titulo: 'Dose da mãe na maternidade',
      passos: [
        'Mãe com sífilis: no cartão **Tratamento da mãe**, clique em **Registrar dose**.',
        'É o mesmo tratamento da aba Tratamento: a dose aplicada no hospital aparece lá, e as demais seguem na UBS.',
      ],
      botoes: ['Registrar dose'],
    },
    {
      id: 'alta',
      titulo: 'Alta',
      passos: ['Clique em **Registrar alta** e informe a data.'],
      botoes: ['Registrar alta'],
    },
  ],
};

export const guiaRecemNascido: Guia = {
  id: 'recem-nascido',
  titulo: 'Recém-nascido: avaliação e conduta',
  resumo:
    'Avaliação clínica, líquor, exames do bebê e a conduta sugerida pelo fluxograma, que o médico confirma.',
  perfis: ['medico', 'enfermeiro'],
  revisadoEm: '2026-10-17',
  secoes: [
    {
      id: 'avaliacao',
      titulo: 'Avaliação clínica',
      passos: [
        'Abra o recém-nascido pela aba **Parto e RN** da mãe ou pelo painel **Neonatologia**.',
        'No cartão **Avaliação clínica**, informe exame físico, hemograma e RX de ossos longos como normal ou alterado, e marque os sinais e sintomas.',
        'No **Líquor**, os valores são opcionais: com leucócitos e proteínas, o sistema sugere normal ou alterado pelos limites do protocolo. Use **Usar a sugestão** ou escolha você.',
        'Clique em **Salvar avaliação**: a sugestão do fluxograma é recalculada.',
      ],
      botoes: ['Usar a sugestão', 'Salvar avaliação'],
    },
    {
      id: 'exames',
      titulo: 'Exames do recém-nascido',
      passos: [
        'Em **Exames do recém-nascido**, clique em **Registrar exame** (VDRL do sangue periférico, VDRL do líquor e outros).',
        'O VDRL do bebê é comparado com o da mãe colhido no mesmo dia.',
      ],
      botoes: ['Registrar exame'],
    },
    {
      id: 'conduta',
      titulo: 'Conduta: o sistema sugere, o médico confirma',
      passos: [
        'O cartão **Sugestão do fluxograma** mostra cada pergunta do fluxograma, a resposta e de onde ela veio. Abra **Como o sistema chegou a esta sugestão** para ver o raciocínio.',
        'O médico escolhe a classificação e clica em **Confirmar conduta**. Escolher diferente da sugestão exige justificativa.',
        'Se entrar um dado novo que muda a sugestão, a conduta fica "a revisar" e o médico confirma de novo.',
      ],
      observacao: 'Só o médico confirma a conduta. O enfermeiro vê a sugestão e registra os dados.',
      botoes: ['Confirmar conduta', 'Como o sistema chegou a esta sugestão'],
      imagem: {
        arquivo: 'rn-fluxograma.png',
        descricao:
          'Página do recém-nascido com a sugestão do fluxograma e a confirmação da conduta.',
      },
    },
    {
      id: 'tratamento',
      titulo: 'Tratamento do recém-nascido',
      passos: [
        'Clique em **Registrar tratamento** e escolha o esquema; o sistema já sugere o da classificação confirmada.',
        'Benzatina em dose única registra a dose aplicada; o tratamento de 10 dias registra início e término (**Concluir tratamento**).',
      ],
      botoes: ['Registrar tratamento', 'Concluir tratamento'],
    },
  ],
};
